import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  CheckCircle,
  Cancel,
  Pending,
  Person,
  LocationCity,
  ExpandMore,
  Phone,
  Telegram,
  Business,
  Group,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchAgentRequests,
  approveAgentRequest,
  rejectAgentRequest,
  setFilters,
  clearFilters,
  setPage,
  setSelectedRequest,
} from '../store/slices/agentRequestsSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { AgentRequest } from '../types';

const AgentRequestsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { requests, pagination, counts, filters, loading, actionLoading, error, selectedRequest } = useSelector(
    (state: RootState) => state.agentRequests
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionRequestId, setActionRequestId] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => {
    dispatch(fetchAgentRequests({ page: 1, limit: 20 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const newFilters: any = {
      page: 1,
      limit: 20,
    };
    if (searchTerm) newFilters.search = searchTerm;
    if (statusFilter) newFilters.status = statusFilter as 'pending' | 'approved' | 'rejected';
    if (cityFilter) newFilters.city = cityFilter;
    
    dispatch(setFilters(newFilters));
    dispatch(fetchAgentRequests(newFilters) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    const newFilters = { ...filters, page: newPage + 1 };
    dispatch(setPage(newPage + 1));
    dispatch(fetchAgentRequests(newFilters) as any);
  };

  const handleViewRequest = (request: AgentRequest) => {
    dispatch(setSelectedRequest(request));
    setShowRequestDetails(true);
  };

  const handleApproveClick = (requestId: string) => {
    setActionRequestId(requestId);
    setAdminNote('');
    setShowApproveModal(true);
  };

  const handleRejectClick = (requestId: string) => {
    setActionRequestId(requestId);
    setAdminNote('');
    setShowRejectModal(true);
  };

  const handleApprove = async () => {
    if (!actionRequestId) return;
    
    try {
      const result = await dispatch(approveAgentRequest({ 
        requestId: actionRequestId, 
        adminNote: adminNote || undefined 
      }) as any);
      if (result.payload?.success) {
        toast.success('Agent request approved successfully');
        setShowApproveModal(false);
        setActionRequestId(null);
        setAdminNote('');
        // Refresh the list
        dispatch(fetchAgentRequests(filters) as any);
      } else {
        toast.error(result.payload?.message || 'Failed to approve request');
      }
    } catch (error) {
      toast.error('An error occurred while approving request');
    }
  };

  const handleReject = async () => {
    if (!actionRequestId || !adminNote.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      const result = await dispatch(rejectAgentRequest({ 
        requestId: actionRequestId, 
        adminNote: adminNote.trim() 
      }) as any);
      if (result.payload?.success) {
        toast.success('Agent request rejected successfully');
        setShowRejectModal(false);
        setActionRequestId(null);
        setAdminNote('');
        // Refresh the list
        dispatch(fetchAgentRequests(filters) as any);
      } else {
        toast.error(result.payload?.message || 'Failed to reject request');
      }
    } catch (error) {
      toast.error('An error occurred while rejecting request');
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    setSearchTerm('');
    setStatusFilter('');
    setCityFilter('');
    dispatch(fetchAgentRequests({ page: 1, limit: 20 }) as any);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'pending': return <Pending />;
      case 'rejected': return <Cancel />;
      default: return <Person />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, mb: { xs: 1, sm: 2 } }}>
        Agent Requests
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 180px' }, minWidth: { xs: 130, sm: 160 } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Pending sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h6">{counts.pending || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 180px' }, minWidth: { xs: 130, sm: 160 } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircle sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">{counts.approved || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Approved
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 180px' }, minWidth: { xs: 130, sm: 160 } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Cancel sx={{ mr: 2, color: 'error.main' }} />
                <Box>
                  <Typography variant="h6">{counts.rejected || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Rejected
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 180px' }, minWidth: { xs: 130, sm: 160 } }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Person sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{pagination.total || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Requests
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search by username, phone, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flex: { xs: 1, sm: '1 1 300px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="City"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationCity fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
              size="small"
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={loading}
              size="small"
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Requests Table */}
      <Paper>
        <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table sx={{ minWidth: { xs: 680, md: 850 } }}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Experience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">No agent requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {request.user?.username || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {request.user?.telegramUsername || 'No Telegram'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationCity fontSize="small" color="action" />
                        <Typography variant="body2">{request.city}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{request.phoneNumber}</Typography>
                        {request.telegramPhoneNumber && (
                          <Typography variant="caption" color="textSecondary">
                            <Telegram fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            {request.telegramPhoneNumber}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {request.hasExperienceWithVirtualGames && (
                          <Chip
                            icon={<Business />}
                            label="Virtual Games"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {request.hasCommunity && (
                          <Chip
                            icon={<Group />}
                            label="Has Community"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {!request.hasExperienceWithVirtualGames && !request.hasCommunity && (
                          <Typography variant="caption" color="textSecondary">
                            No experience
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(request.status)}
                        label={request.status}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {dayjs(request.createdAt).format('MMM DD, YYYY')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {dayjs(request.createdAt).format('HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewRequest(request)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {request.status === 'pending' && (
                          <>
                            <Tooltip title="Approve Request">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApproveClick(request.id)}
                                disabled={actionLoading}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRejectClick(request.id)}
                                disabled={actionLoading}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={(e) => {
            const newFilters = { ...filters, limit: parseInt(e.target.value, 10), page: 1 };
            dispatch(setFilters(newFilters));
            dispatch(fetchAgentRequests(newFilters) as any);
          }}
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Request Details Modal */}
      <Dialog open={showRequestDetails} onClose={() => setShowRequestDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Agent Request Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">User Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Username</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedRequest.user?.username || 'Unknown'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Phone Number</Typography>
                      <Typography variant="body1">{selectedRequest.phoneNumber}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Telegram Username</Typography>
                      <Typography variant="body1">
                        {selectedRequest.user?.telegramUsername || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Telegram ID</Typography>
                      <Typography variant="body1">
                        {selectedRequest.user?.telegramId || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Wallet Balance</Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        {formatCurrency(selectedRequest.user?.wallet || 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Bonus Balance</Typography>
                      <Typography variant="body1" fontWeight="bold" color="info.main">
                        {formatCurrency(selectedRequest.user?.bonusBalance || 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Is Agent</Typography>
                      <Chip
                        label={selectedRequest.user?.isAgent ? 'Yes' : 'No'}
                        color={selectedRequest.user?.isAgent ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Request Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                      <Chip
                        icon={getStatusIcon(selectedRequest.status)}
                        label={selectedRequest.status}
                        color={getStatusColor(selectedRequest.status) as any}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">City</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedRequest.city}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Phone Number</Typography>
                      <Typography variant="body1">{selectedRequest.phoneNumber}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Telegram Phone</Typography>
                      <Typography variant="body1">
                        {selectedRequest.telegramPhoneNumber || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Has Experience</Typography>
                      <Chip
                        label={selectedRequest.hasExperienceWithVirtualGames ? 'Yes' : 'No'}
                        color={selectedRequest.hasExperienceWithVirtualGames ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Has Community</Typography>
                      <Chip
                        label={selectedRequest.hasCommunity ? 'Yes' : 'No'}
                        color={selectedRequest.hasCommunity ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Request Message
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedRequest.requestMessage || 'No message provided'}
                        </Typography>
                      </Paper>
                    </Box>
                    {selectedRequest.adminNote && (
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                          Admin Note
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedRequest.adminNote}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Timestamps</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedRequest.createdAt).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Updated At</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedRequest.updatedAt).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                    {selectedRequest.processedAt && (
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Processed At</Typography>
                        <Typography variant="body1">
                          {dayjs(selectedRequest.processedAt).format('MMM DD, YYYY HH:mm:ss')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRequest?.status === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  if (selectedRequest) {
                    handleApproveClick(selectedRequest.id);
                    setShowRequestDetails(false);
                  }
                }}
                disabled={actionLoading}
                startIcon={<CheckCircle />}
              >
                Approve Request
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  if (selectedRequest) {
                    handleRejectClick(selectedRequest.id);
                    setShowRequestDetails(false);
                  }
                }}
                disabled={actionLoading}
                startIcon={<Cancel />}
              >
                Reject Request
              </Button>
            </>
          )}
          <Button onClick={() => setShowRequestDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onClose={() => setShowApproveModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Agent Request</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Are you sure you want to approve this agent request? You can optionally add an admin note.
            </Typography>
            <TextField
              fullWidth
              label="Admin Note (Optional)"
              multiline
              rows={4}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="e.g., Approved after reviewing application and community verification"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowApproveModal(false);
            setAdminNote('');
            setActionRequestId(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {actionLoading ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onClose={() => setShowRejectModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Agent Request</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Please provide a reason for rejecting this agent request.
            </Typography>
            <TextField
              fullWidth
              label="Admin Note (Required)"
              multiline
              rows={4}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="e.g., Application does not meet minimum requirements"
              required
              error={!adminNote.trim()}
              helperText={!adminNote.trim() ? 'Please provide a reason for rejection' : ''}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowRejectModal(false);
            setAdminNote('');
            setActionRequestId(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={actionLoading || !adminNote.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Cancel />}
          >
            {actionLoading ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentRequestsPage;

